import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["toggle", "nav"];

  connect() {
    this._activeId = null;
    this._ticking = false;
    this._boundOnScroll = this._onScroll.bind(this);
    this._boundOnKey = this._onKey.bind(this);

    window.addEventListener("scroll", this._boundOnScroll, { passive: true });
    window.addEventListener("resize", this._boundOnResize.bind(this), { passive: true });
    window.addEventListener("keydown", this._boundOnKey);

    this._tagMethodLinks();

    requestAnimationFrame(() => {
      const hash = window.location.hash.slice(1).split("?")[0].trim();
      if (hash && document.getElementById(hash)) {
        this._activeId = hash;
        this._highlightSidebarFor(hash);
      } else {
        this._updateActiveSection();
      }
    });
  }

  disconnect() {
    window.removeEventListener("scroll", this._boundOnScroll);
    window.removeEventListener("keydown", this._boundOnKey);
    this._setOpen(false);
  }

  // ── Public actions ────────────────────────────────────────────────────────

  toggle() {
    const isOpen = this.element.classList.contains("sidebar-open");
    this._setOpen(!isOpen);
  }

  close() {
    this._setOpen(false);
  }

  // Close drawer when a nav link is clicked on mobile
  navLinkClick(event) {
    if (this._isMobile() && event.target.closest("a")) {
      this._setOpen(false);
    }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  _isMobile() {
    return window.innerWidth < 768;
  }

  _setOpen(open) {
    this.element.classList.toggle("sidebar-open", open);
    document.body.classList.toggle("sidebar-open", open);
  }

  _onKey(e) {
    if (e.key === "Escape" && this.element.classList.contains("sidebar-open")) {
      this._setOpen(false);
    }
  }

  _boundOnResize() {
    // Auto-close drawer if viewport grows past mobile breakpoint
    if (!this._isMobile()) {
      this._setOpen(false);
    }
    this._onScroll();
  }

  _tagMethodLinks() {
    if (!this.hasNavTarget) return;
    const methods = ["DELETE", "PATCH", "POST", "PUT", "GET"];
    this.navTarget.querySelectorAll("a.docs-sidebar-link").forEach((link) => {
      const text = link.textContent.trim();
      for (const method of methods) {
        if (text.startsWith(method + " ")) {
          const label = text.slice(method.length + 1);
          link.innerHTML = `<span class="cs-http-badge cs-http-badge--${method.toLowerCase()}">${method}</span><span class="cs-http-label">${label}</span>`;
          break;
        }
      }
    });
  }

  _onScroll() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._updateActiveSection();
      this._ticking = false;
    });
  }

  _getSections() {
    const inner = document.querySelector(".docs-content-inner");
    if (!inner) return [];
    return Array.from(inner.querySelectorAll("[id]")).filter(
      (el) => el.id && el.offsetParent !== null
    );
  }

  _updateActiveSection() {
    const sections = this._getSections();
    if (!sections.length) return;

    const offset = 120;
    let active = sections[0];
    for (const el of sections) {
      if (el.getBoundingClientRect().top <= offset) {
        active = el;
      }
    }

    const id = active.id;
    if (!id || id === this._activeId) return;
    this._activeId = id;
    this._highlightSidebarFor(id);
  }

  _getLinkId(href) {
    if (!href) return "";
    const hash = href.indexOf("#");
    if (hash === -1) return "";
    return href.slice(hash + 1).split("?")[0].replace(/\/$/, "").trim();
  }

  _highlightSidebarFor(id) {
    if (!this.hasNavTarget) return;

    const links = this.navTarget.querySelectorAll("a.docs-sidebar-link");
    links.forEach((link) => link.classList.remove("docs-sidebar-link-active"));

    const matches = Array.from(links).filter(
      (link) => this._getLinkId(link.getAttribute("href") || "") === id
    );

    if (!matches.length) return;

    const ancestorDetails = new Set();
    matches.forEach((link) => {
      link.classList.add("docs-sidebar-link-active");
      let el = link.parentElement;
      while (el && el !== this.navTarget) {
        if (el.tagName === "DETAILS") ancestorDetails.add(el);
        el = el.parentElement;
      }
    });

    const allTopSections = this.navTarget.querySelectorAll(
      ":scope > ul > details.docs-sidebar-section"
    );
    allTopSections.forEach((section) => {
      section.open = ancestorDetails.has(section);
    });

    ancestorDetails.forEach((d) => {
      d.open = true;
    });
  }
}
